if (!customElements.get('as-seen-in')) {
  customElements.define(
    'as-seen-in',
    class AsSeenIn extends HTMLElement {
      /** @type {import('swiper').Swiper | null} */
      swiper = null;

      /** @type {(e: KeyboardEvent) => void} */
      boundGroupKeydown = this.onGroupKeydown.bind(this);

      autoplayEnabled = false;
      autoplayPauseOnHover = false;
      autoplayPauseOnInteraction = false;

      boundAutoplayMouseEnter = () => this.swiper?.autoplay?.stop();
      boundAutoplayMouseLeave = () => this.resumeAutoplay();

      constructor() {
        super();
      }

      connectedCallback() {
        this.logoGroupEl = this.querySelector('.asi-swiper');
        this.logoGroupEl?.addEventListener('keydown', this.boundGroupKeydown);

        if (typeof Swiper !== 'undefined') {
          this.initSwiper();
        } else {
          document.addEventListener('swiper-ready', this.initSwiper.bind(this), { once: true });
        }
      }

      disconnectedCallback() {
        this.logoGroupEl?.removeEventListener('keydown', this.boundGroupKeydown);
        if (this.autoplayPauseOnHover) {
          this.removeEventListener('mouseenter', this.boundAutoplayMouseEnter);
          this.removeEventListener('mouseleave', this.boundAutoplayMouseLeave);
        }
        this.swiper?.destroy(true, true);
        this.swiper = null;
      }

      initSwiper() {
        const swiperEl = this.querySelector('.asi-swiper');
        if (!swiperEl) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        
        // Get autoplay settings from data attributes
        const autoplayEnabled = swiperEl.dataset.autoplayEnabled === 'true';
        const autoplayDelay = parseInt(swiperEl.dataset.autoplayDelay, 10) || 3000;
        const autoplayPauseOnInteraction = swiperEl.dataset.autoplayPauseOnInteraction === 'true';
        const autoplayPauseOnHover = swiperEl.dataset.autoplayPauseOnHover === 'true';

        this.autoplayEnabled = autoplayEnabled && !reduceMotion;
        this.autoplayPauseOnHover = autoplayPauseOnHover;
        this.autoplayPauseOnInteraction = autoplayPauseOnInteraction;

        let autoplayConfig = false;
        if (this.autoplayEnabled) {
          autoplayConfig = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        this.swiper = new Swiper(swiperEl, {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 48,
          loop: true,
          autoplay: autoplayConfig,
          breakpoints: {
            768: {
              spaceBetween: 72,
            },
          },
        });

        this.swiper.on('slideChangeTransitionEnd', () => {
          this.activateQuote(this.swiper.realIndex);
          this.resumeAutoplay();
        });

        if (this.autoplayPauseOnHover) {
          this.addEventListener('mouseenter', this.boundAutoplayMouseEnter);
          this.addEventListener('mouseleave', this.boundAutoplayMouseLeave);
        }

        this.setupClickListeners();
      }

      resumeAutoplay() {
        if (!this.autoplayEnabled || !this.swiper?.autoplay) return;
        if (this.autoplayPauseOnHover && this.matches(':hover')) return;
        if (!this.swiper.autoplay.running) {
          this.swiper.autoplay.start();
        }
      }

      pauseAutoplayForInteraction() {
        if (this.autoplayPauseOnInteraction) {
          this.swiper?.autoplay?.stop();
        }
      }

      setupClickListeners() {
        for (const btn of this.querySelectorAll('.asi-logo-btn')) {
          btn.addEventListener('click', this.onLogoClick.bind(this));
        }
      }

      /**
       * @param {MouseEvent} e
       */
      onLogoClick(e) {
        const btn = /** @type {HTMLElement} */ (e.currentTarget);
        const index = parseInt(btn.dataset.asiIndex, 10);
        if (Number.isNaN(index)) return;

        this.pauseAutoplayForInteraction();
        this.swiper?.slideToLoop(index, 400);
        this.activateQuote(index);
      }

      /**
       * @param {KeyboardEvent} e
       */
      onGroupKeydown(e) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        const tabs = [...this.querySelectorAll('.asi-logo-btn')];
        if (!tabs.length) return;
        const activeEl = document.activeElement;
        const i = tabs.indexOf(/** @type {HTMLElement} */ (activeEl));
        if (i < 0) return;
        e.preventDefault();
        let next = i;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        const index = parseInt(tabs[next].dataset.asiIndex, 10);
        if (Number.isNaN(index)) return;
        tabs[next].focus();
        this.pauseAutoplayForInteraction();
        this.swiper?.slideToLoop(index, 0);
        this.activateQuote(index);
      }

      /**
       * @param {number} index
       */
      activateQuote(index) {
        for (const quote of this.querySelectorAll('.asi-quote')) {
          const isActive = parseInt(quote.dataset.asiQuote, 10) === index;
          quote.classList.toggle('asi-quote--active', isActive);
          quote.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        }
        for (const btn of this.querySelectorAll('.asi-logo-btn')) {
          const isActive = parseInt(btn.dataset.asiIndex, 10) === index;
          btn.classList.toggle('asi-logo-btn--active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          btn.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      }
    }
  );
}
